import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useApolloClient} from '@apollo/client';
import {useAuth} from '../../hooks/useAuth';
import {GoogleIcon} from '../shared/Icons/GoogleIcon';
import {useLastVisitedPage} from '../../hooks/useLastVisitedPage.ts';
import {GetCurrentUserRolesDocument, Role} from '../../generated/graphql';
import {canAccessRoute, getDefaultRouteForRole} from '../../utils/routeAccess';
import {
  StaffAccessRefusedError,
  StaffEmailDomainError,
  clearStaffAccessRefusal,
  storedStaffAccessRefusal
} from '../../lib/staffAccessRefusal';

const Login: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const { getLastVisitedPage } = useLastVisitedPage();
  const { signInWithGoogle, error: authError } = useAuth();

  // Tied to the URL (a string), not to every render: a later message must be able to replace it
  useEffect(() => {
    if (new URLSearchParams(location.search).get('error') === 'session_expired') {
      setErrorMessage('Your session has expired. Please sign in again.');
    }
  }, [location.search]);

  // YOJO-3123 [self-registered staff-domain email gets analyst]: the backend refused the login (no
  // staff account). The auth context carries the refusal; after apollo.ts has hard-redirected here
  // (?error=session_expired) only the copy in sessionStorage is left, and it is taken over once.
  const contextRefusal = authError instanceof StaffAccessRefusedError ? authError.message : '';
  const [storedRefusal, setStoredRefusal] = useState(() => (contextRefusal ? '' : storedStaffAccessRefusal() ?? ''));

  // Taken over means removed, so it cannot greet whoever uses this tab next. It stays while the auth
  // context still holds the refusal: the hard redirect that needs it may yet come.
  useEffect(() => {
    if (!contextRefusal) {
      clearStaffAccessRefusal();
    }
  }, []);

  const shownError = contextRefusal || storedRefusal || errorMessage;

  const handleGoogleSignIn = async () => {
    try {
      setErrorMessage('');
      setStoredRefusal('');
      await signInWithGoogle();

      // Fetch user roles to determine valid redirect destination
      const { data } = await apolloClient.query({
        query: GetCurrentUserRolesDocument
      });
      const roles: Role[] = data?.currentUser?.roles || [];

      // Get intended destination and validate against user's roles
      const intendedUrl = location.state?.returnUrl || getLastVisitedPage();
      const targetUrl = canAccessRoute(intendedUrl, roles)
        ? intendedUrl
        : getDefaultRouteForRole(roles);

      navigate(targetUrl, { replace: true });
    } catch (error: any) {
      console.error('Auth error:', error);
      if (error instanceof StaffAccessRefusedError) {
        // Shown from the auth context, which drops it again when a login is accepted
      } else if (error instanceof StaffEmailDomainError) {
        setErrorMessage(error.message);
      } else if (error.code === 'auth/operation-not-allowed') {
        setErrorMessage('Google sign-in is temporarily unavailable. Please contact support.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setErrorMessage('Sign-in window was closed. Please try again.');
      } else {
        setErrorMessage('Authentication failed. Please try again or contact support.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-36 w-auto" src="/logos/logo-slogan-navy.png" alt="Yojo" />
        <h2 className="mt-12 text-center text-3xl font-bold tracking-tight text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            {shownError && (
              <div className="rounded-md bg-red-50 p-4" role="alert" data-testid="login-error">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{shownError}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              className="flex w-full items-center justify-center gap-3 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <GoogleIcon />
              <span>Sign in with Google</span>
            </button>

            <p className="mt-2 text-center text-sm text-gray-600">
              Please use your authorized <span className="font-medium text-blue-600">Yojo email</span> to sign in
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
